const jsonata = require("jsonata");

// Constants for frame and icon configuration
const DEFAULT_VALUES = {
	FRAME_SHAPE: "circle",
	FRAME_COLOR: "gray",
	FRAME_DASH: false,
	FALLBACK_ICON: "icon_map-marker",
	FALLBACK_FRAME: "frame_circle_map_gray"
};

const ENTITY_TYPE_ICONS = {
	track: "icon_ferry",
	camera: "icon_video_camera",
	device: "icon_access-point",
	facility: "icon_facility"
};

const ICON_PREFIX = "icon_";

/**
 * Evaluates a JSONata expression template against provided data
 * @param {string} template - JSONata expression template
 * @param {Object} data - Data object to evaluate against
 * @returns {*} Evaluated result or null if template/data is invalid
 */
const evaluateExpression = (template, data) => {
	if (!template || !data) return null;

	try {
		const expression = jsonata(template);
		return expression.evaluate(data);
	} catch (error) {
		console.warn("Failed to evaluate expression:", template, error);
		return null;
	}
};

/**
 * Generates frame identifier string based on shape, dash, and color
 * @param {string} shape - Frame shape (e.g., "circle", "square")
 * @param {boolean} dash - Whether frame should be dashed
 * @param {string} color - Frame color
 * @returns {string} Generated frame identifier
 */
const generateFrame = (shape, dash, color) => {
	const dashSegment = dash ? "_dashed" : "";
	return `frame_${shape}${dashSegment}_map_${color}`;
};

/**
 * Gets default icon for a specific entity type
 * @param {string} entityType - Type of entity
 * @returns {string} Default icon identifier for the entity type
 */
const getDefaultIconForEntityType = (entityType) => ENTITY_TYPE_ICONS[entityType] || DEFAULT_VALUES.FALLBACK_ICON;

/**
 * Checks if an icon exists in the global sprite data
 * @param {string} iconId - Icon identifier to check
 * @returns {boolean} True if icon exists in sprite data
 */
const iconExistsInSpriteData = (iconId) => !!(global.OC_spriteData && global.OC_spriteData[iconId]);

/**
 * Ensures track icons have the proper "icon_" prefix
 * @param {string} icon - Original icon identifier
 * @param {string} entityType - Entity type
 * @returns {string} Icon with proper prefix if needed
 */
const ensureTrackIconPrefix = (icon, entityType) => {
	if (entityType === "track" && !icon.includes(ICON_PREFIX)) {
		return ICON_PREFIX + icon;
	}
	return icon;
};

/**
 * Extracts frame configuration from entity properties and feed templates
 * @param {Object} entityData - Entity data containing properties
 * @param {Object} entityFeedData - Feed data containing templates
 * @returns {Object} Frame configuration object
 */
const extractFrameConfiguration = (entityData, entityFeedData) => {
	const { frameShapeTemplate, frameDashTemplate, frameColorTemplate, iconTemplate } = entityFeedData || {};
	const { properties } = entityData;

	return {
		frameShape:
			properties?.frameShape || evaluateExpression(frameShapeTemplate, entityData) || DEFAULT_VALUES.FRAME_SHAPE,
		frameDash:
			properties?.frameDash || evaluateExpression(frameDashTemplate, entityData) || DEFAULT_VALUES.FRAME_DASH,
		frameColor:
			properties?.frameColor || evaluateExpression(frameColorTemplate, entityData) || DEFAULT_VALUES.FRAME_COLOR,
		icon: properties?.icon || evaluateExpression(iconTemplate, entityData)
	};
};

/**
 * Creates fallback frame attempts preserving as much original configuration as possible
 * @param {string} originalShape - Original frame shape
 * @param {boolean} originalDash - Original dash setting
 * @param {string} originalColor - Original frame color
 * @returns {Array} Array of fallback frame identifiers in priority order
 */
const createFrameFallbackAttempts = (originalShape, originalDash, originalColor) => [
	// Try with default shape but keep dash and color
	generateFrame(DEFAULT_VALUES.FRAME_SHAPE, originalDash, originalColor),
	// Try with default dash but keep shape and color
	generateFrame(originalShape, DEFAULT_VALUES.FRAME_DASH, originalColor),
	// Try with default color but keep shape and dash
	generateFrame(originalShape, originalDash, DEFAULT_VALUES.FRAME_COLOR),
	// Try with default shape and dash but keep color
	generateFrame(DEFAULT_VALUES.FRAME_SHAPE, DEFAULT_VALUES.FRAME_DASH, originalColor),
	// Try with default shape and color but keep dash
	generateFrame(DEFAULT_VALUES.FRAME_SHAPE, originalDash, DEFAULT_VALUES.FRAME_COLOR),
	// Try with default dash and color but keep shape
	generateFrame(originalShape, DEFAULT_VALUES.FRAME_DASH, DEFAULT_VALUES.FRAME_COLOR),
	// Finally, fall back to completely default
	DEFAULT_VALUES.FALLBACK_FRAME
];

/**
 * Finds the first valid frame from fallback attempts
 * @param {string} preferredFrame - Preferred frame identifier
 * @param {Array} fallbackAttempts - Array of fallback frame identifiers
 * @returns {string} Valid frame identifier
 */
const findValidFrame = (preferredFrame, fallbackAttempts) => {
	// Check if preferred frame exists
	if (iconExistsInSpriteData(preferredFrame)) {
		return preferredFrame;
	}

	// Find first fallback that exists in sprite data
	for (const fallback of fallbackAttempts) {
		if (iconExistsInSpriteData(fallback)) {
			return fallback;
		}
	}

	// Return default fallback if nothing else works
	return DEFAULT_VALUES.FALLBACK_FRAME;
};

/**
 * Processes and validates icon with appropriate fallbacks
 * @param {string} icon - Original icon identifier
 * @param {string} entityType - Entity type
 * @returns {string} Valid icon identifier
 */
const processIcon = (icon, entityType) => {
	// Use entity-specific default if no icon provided
	const iconToProcess = icon || getDefaultIconForEntityType(entityType);

	// Ensure proper prefix for track icons
	const prefixedIcon = ensureTrackIconPrefix(iconToProcess, entityType);

	// Validate icon exists in sprite data
	return iconExistsInSpriteData(prefixedIcon) ? prefixedIcon : DEFAULT_VALUES.FALLBACK_ICON;
};

/**
 * Generates frame and icon identifiers for map entities with intelligent fallback strategy
 *
 * This function processes frame shape, color, icon, and dash properties by:
 * 1. First checking the entity's properties
 * 2. If not found, evaluating feed template expressions
 * 3. Finally applying sensible defaults with progressive fallback
 *
 * @param {Object} entityData - Entity data containing properties and geometry
 * @param {Object} entityFeedData - Feed configuration with templates
 * @param {string} entityType - Type of entity (track, camera, device, etc.)
 * @returns {Object} Object containing validated frame and icon identifiers
 */
const frameShapeAndIconGenerator = (entityData, entityFeedData, entityType) => {
	// Extract frame configuration from various sources
	const frameConfig = extractFrameConfiguration(entityData, entityFeedData);

	// Early return if frame shape or color explicitly set to null
	if (frameConfig.frameShape === null || frameConfig.frameColor === null) {
		return { frame: null, icon: null };
	}

	// Generate preferred frame identifier
	const preferredFrame = generateFrame(frameConfig.frameShape, frameConfig.frameDash, frameConfig.frameColor);

	// Create fallback attempts preserving original configuration where possible
	const fallbackAttempts = createFrameFallbackAttempts(
		frameConfig.frameShape,
		frameConfig.frameDash,
		frameConfig.frameColor
	);

	// Find valid frame with fallback strategy
	const finalFrame = findValidFrame(preferredFrame, fallbackAttempts);

	// Process and validate icon
	const finalIcon = processIcon(frameConfig.icon, entityType);

	return {
		frame: finalFrame,
		icon: finalIcon
	};
};

export default frameShapeAndIconGenerator;
